import { useEffect, useRef } from "preact/hooks";
import { reducedMotion } from "../lib/motion";

type ScopeProps = {
  getTimeDomain: () => Uint8Array | null;
  width?: number;
  height?: number;
};

/** A phosphor trace of the live mic signal — this is the drill's progress
    indicator during mic-scored stages. Falls back to a static 4Hz bar under
    prefers-reduced-motion instead of a racing waveform. */
export function Scope({ getTimeDomain, width = 320, height = 80 }: ScopeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas?.getContext("2d");
    if (!canvas || !g) return;

    const accent = getComputedStyle(canvas).getPropertyValue("--accent").trim() || "#D6F24B";
    const bg = getComputedStyle(canvas).getPropertyValue("--bg-sunken").trim() || "#0A0B07";
    let raf = 0;
    let interval: number | null = null;

    function drawTrace() {
      g!.fillStyle = bg;
      g!.globalAlpha = 0.35;
      g!.fillRect(0, 0, width, height);
      g!.globalAlpha = 1;

      const data = getTimeDomain();
      if (data && data.length) {
        g!.strokeStyle = accent;
        g!.lineWidth = 2;
        g!.beginPath();
        const slice = width / data.length;
        for (let i = 0; i < data.length; i++) {
          const v = data[i]! / 128 - 1;
          const y = height / 2 + v * (height / 2 - 4);
          const x = i * slice;
          if (i === 0) g!.moveTo(x, y);
          else g!.lineTo(x, y);
        }
        g!.stroke();
      }
      raf = requestAnimationFrame(drawTrace);
    }

    function drawBar() {
      const data = getTimeDomain();
      g!.fillStyle = bg;
      g!.fillRect(0, 0, width, height);
      if (!data || !data.length) return;
      let peak = 0;
      for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]! / 128 - 1));
      g!.fillStyle = accent;
      const barHeight = Math.min(height, peak * height * 2);
      g!.fillRect(0, height - barHeight, width, barHeight);
    }

    if (reducedMotion()) {
      drawBar();
      interval = window.setInterval(drawBar, 250);
    } else {
      raf = requestAnimationFrame(drawTrace);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (interval) clearInterval(interval);
    };
  }, [getTimeDomain, width, height]);

  return <canvas ref={canvasRef} width={width} height={height} class="scope" aria-hidden="true" />;
}
